import { useEffect, useState } from 'react'
import { Cancel01Icon as X, Folder01Icon as Folder, Delete02Icon as Trash2, PencilEdit01Icon as Edit2, Clock01Icon as Clock } from 'hugeicons-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useProjectStore } from '@/store/project-store'
import { useNavigate } from 'react-router-dom'

function formatRelativeTime(timestamp: number) {
    const now = Date.now()
    const diff = now - timestamp

    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return `${Math.floor(diff / 86400000)}d ago`
}

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, title, description }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, title: string, description: string }) => {
    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" style={{ WebkitAppRegion: 'no-drag' } as any}>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 10 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 10 }}
                    className="relative bg-surface-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
                >
                    <div className="flex flex-col items-center text-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-danger-500/10 flex items-center justify-center text-danger-500 mb-2">
                            <Trash2 size={24} />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-medium text-white">{title}</h3>
                            <p className="text-sm text-txt-secondary">{description}</p>
                        </div>
                        <div className="flex gap-3 w-full mt-4">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-txt-secondary hover:text-white font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-danger-500 hover:bg-danger-400 text-white font-medium transition-colors shadow-lg shadow-danger-500/20"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}

export default function ProjectSidebar() {
    const navigate = useNavigate()

    const {
        projects,
        currentProjectId,
        isSidebarOpen,
        setIsSidebarOpen,
        loadProjects,
        deleteProject,
        deleteAllProjects,
        renameProject
    } = useProjectStore()

    const [editingId, setEditingId] = useState<string | null>(null)
    const [editName, setEditName] = useState('')
    const [deleteId, setDeleteId] = useState<string | null>(null)
    const [deleteAllConfirm, setDeleteAllConfirm] = useState(false)

    useEffect(() => {
        if (isSidebarOpen) {
            loadProjects()
        }
    }, [isSidebarOpen, loadProjects])


    const handleLoad = (id: string) => {
        setIsSidebarOpen(false)
        navigate(`/editor/${id}`)
    }

    const startRename = (id: string, name: string) => {
        setEditingId(id)
        setEditName(name)
    }

    const handleRename = async (id: string) => {
        if (editName.trim()) {
            await renameProject(id, editName.trim())
        }
        setEditingId(null)
    }

    const handleDelete = () => {
        if (deleteId) {
            deleteProject(deleteId)
            setDeleteId(null)
        }
    }

    if (!isSidebarOpen) return null

    // Group projects by date
    // ... (rest of parsing logic, needs to be re-inserted or carefully sliced, let's just redo the grouping logic here to be safe since it's small)
    const now = Date.now()
    const oneDay = 24 * 60 * 60 * 1000
    const yesterday = now - oneDay
    const lastWeek = now - 7 * oneDay

    // We need to re-implement the grouping or rely on the fact that I'm replacing the whole component logic if I use ReplaceFileContent on the whole file? 
    // The instructions say "RepalcementContent" must be drop in.
    // I will rewrite the component body to include the modal.

    // Actually, I can just rewrite the return statement and the state declarations.
    // But I need to insert the Modal Component *outside* or *inside*? 
    // Best to put it outside.

    // Let's replace the whole file content to be safe and clean.

    const groups = [
        { label: 'Today', projects: projects.filter(p => p.lastModified > yesterday) },
        { label: 'Yesterday', projects: projects.filter(p => p.lastModified <= yesterday && p.lastModified > yesterday - oneDay) },
        { label: 'Previous 7 Days', projects: projects.filter(p => p.lastModified <= yesterday - oneDay && p.lastModified > lastWeek) },
        { label: 'Older', projects: projects.filter(p => p.lastModified <= lastWeek) }
    ].filter(g => g.projects.length > 0)

    return (
        <>
            <AnimatePresence>
                <div className="fixed inset-0 z-[110] pointer-events-none">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsSidebarOpen(false)}
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
                        style={{ WebkitAppRegion: 'no-drag' } as any}
                    />

                    {/* Sidebar */}
                    <motion.div
                        initial={{ x: -320 }}
                        animate={{ x: 0 }}
                        exit={{ x: -320 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="absolute top-0 bottom-0 left-0 w-80 bg-[#0A0A0C] border-r border-white/10 shadow-2xl pointer-events-auto flex flex-col"
                        style={{ WebkitAppRegion: 'no-drag' } as any}
                    >
                        <div className="p-6 flex items-center justify-between border-b border-white/5">
                            <h2 className="text-xl font-semibold text-white">Projects</h2>
                            <div className="flex items-center gap-1">
                                {projects.length > 0 && (
                                    <button
                                        onClick={() => setDeleteAllConfirm(true)}
                                        className="p-2 hover:bg-danger-500/20 rounded-lg text-txt-dim hover:text-danger-400 transition-colors"
                                        title="Delete all projects"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsSidebarOpen(false)}
                                    className="p-2 hover:bg-white/5 rounded-lg text-txt-dim hover:text-white transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>


                        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-6 scrollbar-thin scrollbar-thumb-white/10">
                            {groups.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                                    <Folder size={48} className="text-white/5" />
                                    <div className="space-y-1">
                                        <p className="text-sm text-txt-secondary font-medium">No projects yet</p>
                                        <p className="text-xs text-txt-dim">Your screen recordings will appear here</p>
                                    </div>
                                </div>
                            ) : (
                                groups.map((group) => (
                                    <div key={group.label} className="space-y-2">
                                        <h3 className="text-[10px] uppercase tracking-wider text-txt-dim font-bold px-2">{group.label}</h3>
                                        <div className="space-y-1">
                                            {group.projects.map(project => (
                                                <div
                                                    key={project.id}
                                                    className={`group relative flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${currentProjectId === project.id ? 'bg-brand-500/10 border border-brand-500/20' : 'hover:bg-white/5 border border-transparent'}`}
                                                    onClick={() => handleLoad(project.id)}
                                                >
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${currentProjectId === project.id ? 'bg-brand-500 text-white' : 'bg-surface-800 text-txt-dim group-hover:text-white'}`}>
                                                        <Folder size={20} />
                                                    </div>

                                                    <div className="flex-1 min-w-0 text-left">
                                                        {editingId === project.id ? (
                                                            <input
                                                                autoFocus
                                                                className="w-full bg-surface-800 border border-brand-500 rounded px-1.5 py-0.5 text-sm text-white focus:outline-none"
                                                                value={editName}
                                                                onChange={e => setEditName(e.target.value)}
                                                                onBlur={() => handleRename(project.id)}
                                                                onKeyDown={e => e.key === 'Enter' && handleRename(project.id)}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        ) : (
                                                            <>
                                                                <div className="text-sm font-medium text-white truncate">{project.name}</div>
                                                                <div className="text-[10px] text-txt-dim flex items-center gap-1 mt-0.5">
                                                                    <Clock size={10} />
                                                                    {formatRelativeTime(project.lastModified)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                startRename(project.id, project.name)
                                                            }}
                                                            className="p-1.5 hover:bg-white/10 rounded-md text-txt-dim hover:text-white"
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setDeleteId(project.id)
                                                            }}
                                                            className="p-1.5 hover:bg-danger-500/20 rounded-md text-txt-dim hover:text-danger-400"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </motion.div>
                </div>
            </AnimatePresence>

            <DeleteConfirmationModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Delete Project?"
                description="This action cannot be undone. This will permanently delete your project and all associated files."
            />
            <DeleteConfirmationModal
                isOpen={deleteAllConfirm}
                onClose={() => setDeleteAllConfirm(false)}
                onConfirm={() => { deleteAllProjects(); setDeleteAllConfirm(false) }}
                title="Delete All Projects?"
                description="This action cannot be undone. This will permanently delete all your projects and their associated files."
            />
        </>
    )
}
